import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Row,
  Col,
  Form,
  Spinner,
  InputGroup,
  Alert,
  Badge,
} from "react-bootstrap";
import { FaCheck, FaSearch, FaTimes } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI } from "../../api/apiSystem";
import ClientChatBox from "../chat/ClientChatBox";
import "../clientTable/ClientTable.css";
import { formatNumber } from "../../utils/helpers";
import PaginationComponent from "../PaginationComponent";
import { useSearchFilter } from "../useSearchFilter";

export default function EmployeeBookingsTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { employeeId } = useParams();

  const [userType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const [activeTab, setActiveTab] = useState("booking");
  const [bookingData, setBookingData] = useState([]);
  const [furnitureData, setFurnitureData] = useState([]);
  const [carData, setCarData] = useState([]);
  const [countData, setCountData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredBookingData = useSearchFilter(bookingData, searchTerm, ["order_id"]);
  const filteredFurnitureData = useSearchFilter(furnitureData, searchTerm, ["order_id"]);
  const filteredCarData = useSearchFilter(carData, searchTerm, ["order_id"]);

  const [pagination, setPagination] = useState({
    current_page: 1, last_page: 1, per_page: 10, total: 0,
  });
  const [furniturePagination, setFurniturePagination] = useState({
    current_page: 1, last_page: 1, per_page: 10, total: 0,
  });
  const [carPagination, setCarPagination] = useState({
    current_page: 1, last_page: 1, per_page: 10, total: 0,
  });

  // ✅ إصلاح: load الصفحة الأولى عند تغيير التاب
  useEffect(() => {
    if (!employeeId) return;
    if (activeTab === "booking") fetchBookings(1);
    else if (activeTab === "furniture") fetchFurnitureBookings(1);
    else if (activeTab === "car") fetchCarBookings(1);
  }, [activeTab, employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    fetchCounts();
  }, [employeeId]);

  // ✅ إصلاح: تمرير page بشكل صحيح
  const fetchBookings = async (page = 1) => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const response = await userAPI.employees.bookings(employeeId, {
        page,
        per_page: pagination.per_page,
      });
      if (response.success) {
        const data = response.data.data || response.data;
        setBookingData(data);
        setPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      } else {
        Swal.fire({
          icon: "error",
          title: t("clientTable.errors.error"),
          text: response.message || t("clientTable.errors.loadBookingsFailed"),
          confirmButtonColor: "#ff7b00",
        });
      }
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: t("clientTable.errors.connectionFailed"),
        confirmButtonColor: "#ff7b00",
      });
    } finally {
      setLoading(false);
    }setBookingData
  };

  const fetchFurnitureBookings = async (page = 1) => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const response = await userAPI.employees.furnitures(employeeId, {
        page,
        per_page: furniturePagination.per_page,
      });
      if (response.success) {
        const data = response.data.data || response.data;
        setFurnitureData(data);
        (data);
        setFurniturePagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      }
    } catch (error) {
      console.error("Error loading furniture:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCarBookings = async (page = 1) => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const response = await userAPI.employees.cars(employeeId, {
        page,
        per_page: carPagination.per_page,
      });
      if (response.success) {
        const data = response.data.data || response.data;
        setCarData(data);
        setCarPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.data?.length || 0,
          },
        );
      }
    } catch (error) {
      console.error("Error loading cars:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    if (!employeeId) return;
    try {
      const response = await userAPI.employees.counts(employeeId);
      if (response.success) {
        setCountData(response.data.data || response.data);
      }
    } catch (error) {
      console.error("Error loading counts:", error);
    }
  };

  // ✅ view-only
  const handleBookingDetails = async (row, type = "booking") => {
    try {
      const { transportId, orderId } = getTransportAndOrderIds(row);
      const detailsId =
        type === "furniture" || type === "cars"
          ? transportId
          : row.booking?.id || row.id;

      let response;
      if (type === "furniture" || type === "cars") {
        response = await userAPI.employees.showTransport(employeeId, detailsId);
      } else {
        response = await userAPI.employees.showbooking(employeeId, detailsId);
      }

      if (!response.success) {
        throw new Error(response.message || t("clientTable.errors.loadDetailsFailed"));
      }

      if (type === "furniture" || type === "cars") {
        navigate(
          `/user/employees/${employeeId}/transport/transport_details/${detailsId}`,
          {
            state: {
              data: response.data,
              type,
              transportId: detailsId,
              orderId,
              fromPage: pagination.current_page,
              activeTab,
            },
          },
        );
      } else {
        navigate(
          `/user/employees/${employeeId}/booking/booking_details/${detailsId}`,
          {
            state: {
              data: response.data,
              type,
              fromPage: pagination.current_page,
              activeTab,
            },
          },
        );
      }
    } catch (error) {
      console.error("Error loading details:", error);
      Swal.fire({
        icon: "error",
        title: t("clientTable.errors.connectionError"),
        text: error.message || t("clientTable.errors.tryAgainLater"),
        confirmButtonColor: "#ff7b00",
      });
    }
  };

  const getTransportAndOrderIds = (order) => {
    if (order.transport?.id) return { transportId: order.transport.id, orderId: order.id };
    if (order.transport_id) return { transportId: order.transport_id, orderId: order.id };
    return { transportId: order.id, orderId: order.id };
  };

  const clearSearch = () => setSearchTerm("");

  const getCurrentData = () => {
    if (activeTab === "furniture") return filteredFurnitureData;
    if (activeTab === "car") return filteredCarData;
    return filteredBookingData;
  };

  const getCurrentPagination = () => {
    if (activeTab === "furniture") return furniturePagination;
    if (activeTab === "car") return carPagination;
    return pagination;
  };

  // ✅ إصلاح: handlePageChange يمرر employeeId صح
  const handlePageChange = (page) => {
    if (activeTab === "furniture") fetchFurnitureBookings(page);
    else if (activeTab === "car") fetchCarBookings(page);
    else fetchBookings(page);
  };

  const getBookingLocation = (row) => {
    if (row.transport) {
      return {
        from: row.transport.booking_from,
        to: row.transport.booking_to,
        distance: row.transport.distance,
        transport_id: row.transport.id,
      };
    }
    return {
      from: row.booking_from,
      to: row.booking_to,
      distance: row.distance,
      transport_id: row.transport_id,
    };
  };

  const getStatusBadge = (status) => {
    const config = {
      cancelled: { variant: "danger", text: t("cancelled") },
      rejected: { variant: "danger", text: t("rejected") },
      assigned: { variant: "info", text: t("assigned") },
      received: { variant: "secondary", text: t("received") },
      completed: { variant: "success", text: t("completed") },
      pending: { variant: "warning", text: t("pending") },
      approved: { variant: "success", text: t("approved") },
    }[status] || { variant: "light", text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  return (
    <>
      <NavbarMenu />
      <Container fluid className="py-4">
        <h4 className="page-title mb-4" style={{ color: "#fff" }}>
          {/* {t("clientTable.title")} */}
          {countData?.user}
        </h4>

        <div className="p-4">
          {/* Tabs */}
          <div className="dark-tabs-container">
            {["booking", "furniture", "car"]
              .filter((tab) => {
                if (!countData) return true;
                if (tab === "booking") return (countData.bookings || 0) > 0;
                if (tab === "furniture") return (countData.furniture_transports || 0) > 0;
                if (tab === "car") return (countData.car_transports || 0) > 0;
                return true;
              })
              .map((tab) => (
                <Button
                  key={tab}
                  size="sm"
                  onClick={() => setActiveTab(tab)}
                  className={`dark-tab-btn ${activeTab === tab ? "active" : ""}`}
                >
                  {t(`clientTable.tabs.${tab}`)}
                  {countData && (
                    <span className="ms-2 badge bg-success rounded-circle">
                      {tab === "booking"
                        ? countData.bookings
                        : tab === "furniture"
                        ? countData.furniture_transports
                        : countData.car_transports}
                    </span>
                  )}
                </Button>
              ))}
          </div>

          {/* Search */}
          <div className="dark-table-controls mb-3 mt-4">
            <div className="entries-control">
              <span>{t("clientTable.show")}</span>
              <Form.Select size="sm" className="dark-search-input" style={{ width: "80px" }}>
                <option>10</option>
                <option>25</option>
                <option>50</option>
              </Form.Select>
              <span>{t("clientTable.entries")}</span>
            </div>
            <InputGroup className="dark-input-group" style={{ maxWidth: "350px" }}>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t("clientTable.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dark-search-input"
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={clearSearch}>
                  ✕
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Table */}
          <div className="table-responsive">
            {loading ? (
              <div className="dark-loading py-5">
                <Spinner animation="border" />
                <p className="mt-3" style={{ color: "#c5c5c5" }}>
                  {t("clientTable.loading") || "Loading..."}
                </p>
              </div>
            ) : (
              <Table hover responsive className="align-middle text-center orders-table">
                <thead>
                  <tr className="fw-bold">
                    <th>#</th>
                    <th>{t("clientTable.tableHeaders.orderId")}</th>
                    <th>{t("company_name")}</th>
                    <th>{t("clientTable.tableHeaders.pickup")}</th>
                    <th>{t("clientTable.tableHeaders.delivery")}</th>
                    <th>{t("clientTable.tableHeaders.distance")}</th>
                    <th>{t("clientTable.tableHeaders.totalWeight")}</th>
                    <th>{t("clientTable.tableHeaders.paymentStatus")}</th>
                    <th>{t("clientTable.tableHeaders.status")}</th>
                    <th>{t("clientTable.tableHeaders.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentData().map((row, idx) => {
                    const location = getBookingLocation(row);
                    const orderId = row.order_id || row.booking_id;
                    const paymentStatus =
                      row.payment_status === 1 || row.payment === "Bezahlt" || row.bezahlt;

                    return (
                      <tr key={idx}>
                        <td>{row.id}</td>
                        <td>{orderId}</td>
                        <td>{row.user?.user_name || "—"}</td>
                        <td>{location.from || row.abholung || "—"}</td>
                        <td>{location.to || row.zustellung || "—"}</td>
                        <td>{location.distance || row.strecke || "—"}</td>
                        <td>{formatNumber(location.total_weight || row.total_weight)} KG</td>
                        <td>
                          {paymentStatus ? (
                            <span className="text-success fw-bold">
                              {t("clientTable.paymentStatus.paid")} <FaCheck />
                            </span>
                          ) : (
                            <span className="text-danger fw-bold">
                              {t("clientTable.paymentStatus.unpaid")} <FaTimes />
                            </span>
                          )}
                        </td>
                        <td>{getStatusBadge(row.status)}</td>
                        <td>
                          <Button
                            size="sm"
                            className="action-btn-view"
                            onClick={() => {
                              const type =
                                activeTab === "furniture"
                                  ? "furniture"
                                  : activeTab === "car"
                                  ? "cars"
                                  : "booking";
                              handleBookingDetails(row, type);
                            }}
                          >
                            {t("clientTable.buttons.view")}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {getCurrentPagination().total > 0 && (
              <div className="d-flex justify-content-center mt-4">
                <PaginationComponent
                  currentPage={getCurrentPagination().current_page}
                  lastPage={getCurrentPagination().last_page}
                  onPageChange={handlePageChange}
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </div>
      </Container>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}